"use client";

import React, { useMemo } from "react";
import {
  addHours,
  areIntervalsOverlapping,
  differenceInMinutes,
  eachDayOfInterval,
  eachHourOfInterval,
  endOfWeek,
  format,
  getHours,
  getMinutes,
  isBefore,
  isSameDay,
  isToday,
  startOfDay,
  startOfWeek,
} from "date-fns";

import { cn } from "../../lib/utils";
import { DraggableEvent } from "./draggable-event";
import { DroppableCell } from "./droppable-cell";
import { EventItem } from "./event-item";
import { isMultiDayEvent } from "./utils";
import { useCurrentTimeIndicator } from "./hooks/use-current-time-indicator";
import {
  WeekCellsHeight,
  EndHour,
  StartHour,
  TimeSlotIntervalMinutes,
} from "./constants";
import {
  formatDateString,
  formatTimeString,
  resolveTimeZone,
} from "./time-utils";

/**
 * @typedef {import("./types").CalendarEvent} CalendarEvent
 */

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export function WeekView({
  currentDate,
  events,
  onEventSelect,
  onEventCreate,
  timezone,
  timeFormat = "24h",
  weekStartsOn = 1,
  businessHours,
}) {
  const resolvedTimeZone = resolveTimeZone(timezone);
  const businessStartHour = businessHours?.startHour ?? StartHour;
  const businessEndHour = businessHours?.endHour ?? EndHour;
  const daySchedules = businessHours?.byDay || {};
  const segmentsPerHour = Math.max(1, Math.round(60 / TimeSlotIntervalMinutes));
  const days = useMemo(() => {
    const weekStartDate = startOfWeek(currentDate, { weekStartsOn });
    const weekEndDate = endOfWeek(currentDate, { weekStartsOn });
    return eachDayOfInterval({ start: weekStartDate, end: weekEndDate });
  }, [currentDate, weekStartsOn]);

  const weekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn }),
    [currentDate, weekStartsOn]
  );

  const hours = useMemo(() => {
    const dayStart = startOfDay(currentDate);
    const start = addHours(dayStart, Math.floor(businessStartHour));
    const end = addHours(
      dayStart,
      Math.max(Math.ceil(businessEndHour) - 1, Math.floor(businessStartHour))
    );
    return eachHourOfInterval({ start, end });
  }, [currentDate, businessStartHour, businessEndHour]);

  const allDayEvents = useMemo(() => {
    return events
      .filter((event) => event.allDay || isMultiDayEvent(event))
      .filter((event) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);
        return days.some(
          (day) =>
            isSameDay(day, eventStart) ||
            isSameDay(day, eventEnd) ||
            (day > eventStart && day < eventEnd)
        );
      });
  }, [events, days]);

  const processedDayEvents = useMemo(() => {
    return days.map((day) => {
      const dayEvents = events.filter((event) => {
        if (event.allDay || isMultiDayEvent(event)) return false;

        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        return (
          isSameDay(day, eventStart) ||
          isSameDay(day, eventEnd) ||
          (eventStart < day && eventEnd > day)
        );
      });

      const sortedEvents = [...dayEvents].sort((a, b) => {
        const aStart = new Date(a.start);
        const bStart = new Date(b.start);
        const aEnd = new Date(a.end);
        const bEnd = new Date(b.end);

        if (aStart < bStart) return -1;
        if (aStart > bStart) return 1;

        const aDuration = differenceInMinutes(aEnd, aStart);
        const bDuration = differenceInMinutes(bEnd, bStart);
        return bDuration - aDuration;
      });

      const positionedEvents = [];
      const dayStart = startOfDay(day);
      const columns = [];

      sortedEvents.forEach((event) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        const adjustedStart = isSameDay(day, eventStart)
          ? eventStart
          : dayStart;
        const adjustedEnd = isSameDay(day, eventEnd)
          ? eventEnd
          : addHours(dayStart, 24);

        const startHour =
          getHours(adjustedStart) + getMinutes(adjustedStart) / 60;
        const endHour = getHours(adjustedEnd) + getMinutes(adjustedEnd) / 60;

        const top = (startHour - businessStartHour) * WeekCellsHeight;
        const height = (endHour - startHour) * WeekCellsHeight;

        let columnIndex = 0;
        let placed = false;

        while (!placed) {
          const col = columns[columnIndex] || [];
          if (col.length === 0) {
            columns[columnIndex] = col;
            placed = true;
          } else {
            const overlaps = col.some((c) =>
              areIntervalsOverlapping(
                { start: adjustedStart, end: adjustedEnd },
                {
                  start: new Date(c.event.start),
                  end: new Date(c.event.end),
                }
              )
            );
            if (!overlaps) {
              placed = true;
            } else {
              columnIndex++;
            }
          }
        }

        const currentColumn = columns[columnIndex] || [];
        columns[columnIndex] = currentColumn;
        currentColumn.push({ event, end: adjustedEnd });

        const width = columnIndex === 0 ? 1 : 1 - columnIndex * 0.1;
        const left = columnIndex === 0 ? 0 : columnIndex * 0.1;

        positionedEvents.push({
          event,
          top,
          height,
          left,
          width,
          zIndex: 10 + columnIndex,
        });
      });

      return positionedEvents;
    });
  }, [days, events]);

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    onEventSelect(event);
  };

  const showCurrentTimeForDay = (day) => {
    const dayKey = DAY_KEYS[day.getDay()];
    const schedule = daySchedules[dayKey];
    if (schedule && schedule.enabled === false) {
      return false;
    }
    const now = new Date();
    return isSameDay(day, now) || isToday(day);
  };

  const { currentTimePosition, currentTimeVisible } = useCurrentTimeIndicator(
    currentDate,
    "week",
    {
      startHour: businessStartHour,
      endHour: businessEndHour,
      weekStartsOn,
    }
  );

  // const timezoneDisplay = formatDateString(new Date(), resolvedTimeZone, {
  //   timeZoneName: "short",
  // });

  const timezoneDisplay = useMemo(() => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: resolvedTimeZone,
      timeZoneName: "short",
    }).formatToParts(new Date());

    // e.g., "GMT+3", "GMT+03:00", "UTC+3"
    const raw = parts.find((p) => p.type === "timeZoneName")?.value ?? "";

    // Normalize to "GMT +3" style
    return raw.replace(/^(GMT|UTC)([+-])/, "$1 $2").replace(/:00$/, "");
  }, [resolvedTimeZone]);

  return (
    <div data-slot="week-view" className="contents">
      <div className="border-border/70 sticky top-0 z-30 grid grid-cols-[4rem_repeat(7,_1fr)] border-t bg-background/90 backdrop-blur">
        <div className="border-border/70 border-b flex items-center justify-center py-2 text-[10px] text-muted-foreground">
          <span>{timezoneDisplay}</span>
        </div>
        {days.map((day) => {
          const isTodayCell = isToday(day);
          const isSelected = isSameDay(day, currentDate);
          return (
            <div
              key={day.toString()}
              className="border-border/70 border-b py-2 text-sm font-medium last:border-r-0"
            >
              <div className="flex items-center justify-center gap-1">
                <span className="text-muted-foreground/80 text-xs uppercase tracking-wide">
                  <span className="sm:hidden" aria-hidden="true">
                    {format(day, "E")[0]}
                  </span>
                  <span className="max-sm:hidden">{format(day, "EEE")}</span>
                </span>
                <span
                  data-today={isTodayCell || undefined}
                  data-selected={isSelected || undefined}
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full text-sm font-medium transition-colors",
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : isTodayCell
                      ? "bg-muted text-foreground"
                      : "text-foreground"
                  )}
                >
                  {format(day, "d")}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex-1 overflow-hidden">
        {allDayEvents.length > 0 && (
          <div className="border-border/70 bg-muted/40 border-b">
            <div className="grid grid-cols-[4rem_repeat(7,_1fr)]">
              <div className="border-border/70 relative border-r flex items-center justify-center p-0">
                <span className="text-muted-foreground/70 w-full px-0 text-center uppercase text-[10px] sm:text-[10px]">
                  All day
                </span>
              </div>
              {days.map((day, dayIndex) => {
                const dayAllDayEvents = allDayEvents.filter((event) => {
                  const eventStart = new Date(event.start);
                  const eventEnd = new Date(event.end);
                  return (
                    isSameDay(day, eventStart) ||
                    isSameDay(day, eventEnd) ||
                    (day > eventStart && day < eventEnd)
                  );
                });

                return (
                  <div
                    key={day.toString()}
                    className="border-border/70 relative border-r p-2 last:border-r-0"
                    data-today={isToday(day) || undefined}
                  >
                    {dayAllDayEvents.map((event) => {
                      const eventStart = new Date(event.start);
                      const eventEnd = new Date(event.end);
                      const isFirstDay = isSameDay(day, eventStart);
                      const isLastDay = isSameDay(day, eventEnd);
                      const isFirstVisibleDay =
                        dayIndex === 0 && isBefore(eventStart, weekStart);
                      const shouldShowTitle = isFirstDay || isFirstVisibleDay;

                      return (
                        <EventItem
                          key={`allday-${event.id}-${day.toISOString()}`}
                          event={event}
                          view="month"
                          isFirstDay={isFirstDay}
                          isLastDay={isLastDay}
                          onClick={(e) => handleEventClick(event, e)}
                          timezone={resolvedTimeZone}
                          timeFormat={timeFormat}
                        >
                          <div
                            className={cn(
                              "text-xs font-medium",
                              !shouldShowTitle && "invisible"
                            )}
                            aria-hidden={!shouldShowTitle}
                          >
                            {event.title}
                          </div>
                        </EventItem>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-[4rem_repeat(7,_1fr)]">
          <div className="border-border/70 border-r">
            {hours.map((hour, index) => (
              <div
                key={hour.toISOString()}
                className="border-border/70 relative min-h-[var(--week-cells-height)] border-b last:border-b-0"
              >
                <span
                  className={cn(
                    "bg-background text-muted-foreground/70 absolute left-0 flex h-6 w-16 max-w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs",
                    index === 0 ? "top-1" : "-top-3"
                  )}
                >
                  {formatTimeString(hour, resolvedTimeZone, timeFormat, {
                    omitMinutesWhenZero: true,
                  })}
                </span>
              </div>
            ))}
          </div>
          {days.map((day, dayIndex) => {
            const positionedEvents = processedDayEvents[dayIndex] || [];
            const dayKey = DAY_KEYS[day.getDay()];
            const daySchedule = daySchedules[dayKey] || {};
            const dayEnabled = daySchedule.enabled !== false;
            const dayStartHour =
              daySchedule.startHour ?? businessStartHour;
            const dayEndHour = Math.max(
              daySchedule.endHour ?? businessEndHour,
              dayStartHour + 1
            );
            return (
              <div
                key={day.toString()}
                className={cn(
                  "border-border/70 relative border-r last:border-r-0",
                  !dayEnabled && "bg-muted/40"
                )}
              >
                {!dayEnabled && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Clinic closed
                  </div>
                )}
                {positionedEvents.map((positionedEvent) => (
                  <div
                    key={positionedEvent.event.id}
                    className="absolute z-10 px-0.5"
                    style={{
                      top: `${positionedEvent.top}px`,
                      height: `${positionedEvent.height}px`,
                      left: `${positionedEvent.left * 100}%`,
                      width: `${positionedEvent.width * 100}%`,
                      zIndex: positionedEvent.zIndex,
                    }}
                  >
                    <div className="h-full w-full">
                      <DraggableEvent
                        event={positionedEvent.event}
                        view="week"
                        onClick={(e) =>
                          handleEventClick(positionedEvent.event, e)
                        }
                        showTime
                        height={positionedEvent.height}
                        timezone={resolvedTimeZone}
                        timeFormat={timeFormat}
                      />
                    </div>
                  </div>
                ))}

                {showCurrentTimeForDay(day) && currentTimeVisible && (
                  <div
                    className="pointer-events-none absolute right-0 left-0 z-20"
                    style={{ top: `${currentTimePosition}%` }}
                  >
                    <div className="relative flex items-center">
                      <div className="bg-primary absolute -left-1 h-2 w-2 rounded-full"></div>
                      <div className="bg-primary h-[2px] w-full"></div>
                    </div>
                  </div>
                )}

                {hours.map((hour) => {
                  const hourValue = getHours(hour);
                  return (
                    <div
                      key={hour.toISOString()}
                      className="border-border/70 relative min-h-[var(--week-cells-height)] border-b last:border-b-0"
                    >
                      {Array.from({ length: segmentsPerHour }).map(
                        (_, segment) => {
                          const minuteOffset =
                            segment * TimeSlotIntervalMinutes;
                          const segmentTime = hourValue + minuteOffset / 60;
                          const slotWithinSchedule =
                            dayEnabled &&
                            segmentTime >= dayStartHour &&
                            segmentTime < dayEndHour;

                          return (
                            <DroppableCell
                              key={`${hour.toISOString()}-${segment}`}
                              id={`week-cell-${day.toISOString()}-${segmentTime}`}
                              date={day}
                              time={segmentTime}
                              className={cn(
                                "absolute w-full",
                                segment === 0 && "top-0",
                                !slotWithinSchedule &&
                                  "pointer-events-none opacity-40"
                              )}
                              style={{
                                height: `${100 / segmentsPerHour}%`,
                                top: `${(segment / segmentsPerHour) * 100}%`,
                              }}
                              disabled={!slotWithinSchedule}
                              onClick={() => {
                                if (!slotWithinSchedule) return;
                                const startTime = new Date(day);
                                startTime.setHours(hourValue);
                                startTime.setMinutes(minuteOffset);
                                onEventCreate(startTime);
                              }}
                            />
                          );
                        }
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
